/* Hume collection — pills, Shopify filters (AJAX section rendering), drawer, card CTA (add to cart / checkout) */
(() => {
  if (window.__humeCollectionLoaded) return;
  window.__humeCollectionLoaded = true;

  const SEL = '[data-hc-collection]';
  const CTA = '[data-hc-cta]';
  const SOURCE = 'hume-collection';
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };

  /* ---------------- Quick filter pills (client side) ---------------- */
  const matches = (item, pill) => {
    const mode = pill.dataset.mode;
    const tag = pill.dataset.tag;
    const pre = item.dataset.preorder === 'true';
    const avail = item.dataset.available === 'true';
    switch (mode) {
      case 'ready': return avail && !pre;
      case 'preorder': return pre;
      case 'in_stock': return avail;
      case 'out_of_stock': return !avail;
      case 'on_sale': return item.dataset.onsale === 'true';
      case 'has_tag': return !!tag && item.dataset.tags.includes(`|${tag}|`);
      case 'missing_tag': return !tag || !item.dataset.tags.includes(`|${tag}|`);
      default: return true;
    }
  };

  const applyPill = (section, pill, updateUrl) => {
    const pills = [...section.querySelectorAll('[data-hc-pill]')];
    const items = [...section.querySelectorAll('[data-hc-item]')];
    const promos = section.querySelectorAll('[data-hc-promo]');
    const empty = section.querySelector('[data-hc-empty]');
    if (!pill) return;

    pills.forEach(p => p.setAttribute('aria-pressed', p === pill ? 'true' : 'false'));
    let visible = 0;
    items.forEach(item => {
      const show = matches(item, pill);
      item.hidden = !show;
      if (show) visible++;
    });

    const isFirst = pill === pills[0];
    const hidePromo = section.dataset.promoHide === 'true' && !isFirst;
    promos.forEach(p => { p.hidden = hidePromo; });
    if (empty) empty.hidden = visible > 0 || items.length === 0;

    if (updateUrl && section.dataset.syncUrl === 'true') {
      const url = new URL(window.location.href);
      if (isFirst) url.searchParams.delete('view'); else url.searchParams.set('view', pill.dataset.key);
      history.replaceState(history.state, '', url);
    }
  };

  const initPills = (section) => {
    const pills = [...section.querySelectorAll('[data-hc-pill]')];
    if (!pills.length) return;
    const items = [...section.querySelectorAll('[data-hc-item]')];

    pills.forEach(pill => {
      const count = items.filter(i => matches(i, pill)).length;
      const c = pill.querySelector('[data-hc-count]');
      if (c) c.textContent = count;
      if (section.dataset.hideEmptyPills === 'true' && count === 0 && pill !== pills[0]) pill.hidden = true;
    });

    const key = new URL(window.location.href).searchParams.get('view');
    const start = (key && pills.find(p => p.dataset.key === key && !p.hidden)) || pills[0];
    applyPill(section, start, false);
  };

  /* ---------------- Section rendering for Shopify filters ---------------- */
  const renderFromUrl = async (section, url, push = true) => {
    const sectionId = section.dataset.sectionId;
    const drawerOpen = !!section.querySelector('[data-hc-drawer]:not([hidden])');
    const drawerScroll = section.querySelector('.hc-drawer__body')?.scrollTop || 0;
    const fetchUrl = new URL(url, window.location.origin);
    const view = new URL(window.location.href).searchParams.get('view');
    if (view && !fetchUrl.searchParams.has('view')) fetchUrl.searchParams.set('view', view);
    const pageUrl = fetchUrl.toString();
    fetchUrl.searchParams.set('section_id', sectionId);

    section.classList.add('is-loading');
    try {
      const res = await fetch(fetchUrl.toString(), { credentials: 'same-origin' });
      if (!res.ok) throw new Error(res.status);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector(SEL);
      if (!fresh) throw new Error('Section not found');
      section.replaceWith(fresh);
      initPills(fresh);
      initCtas(fresh);
      if (drawerOpen) {
        openDrawer(fresh, false);
        const body = fresh.querySelector('.hc-drawer__body');
        if (body) body.scrollTop = drawerScroll;
      }
      if (push) history.pushState({ hc: true }, '', pageUrl);
    } catch (e) {
      window.location.href = pageUrl; // graceful fallback
    }
  };

  const formToUrl = (form) => {
    const params = new URLSearchParams();
    new FormData(form).forEach((v, k) => { if (String(v).trim() !== '') params.append(k, v); });
    const action = form.getAttribute('action');
    const qs = params.toString();
    return qs ? `${action}?${qs}` : action;
  };

  /* ---------------- Drawer ---------------- */
  let lastFocus = null;
  function openDrawer(section, focus = true) {
    const d = section.querySelector('[data-hc-drawer]');
    if (!d) return;
    if (focus) lastFocus = document.activeElement;
    d.hidden = false;
    document.documentElement.classList.add('hc-lock');
    if (focus) d.querySelector('.hc-drawer__close')?.focus();
  }
  function closeDrawer(section) {
    const d = section.querySelector('[data-hc-drawer]');
    if (!d || d.hidden) return;
    d.hidden = true;
    document.documentElement.classList.remove('hc-lock');
    const opener = section.querySelector('[data-hc-drawer-open]');
    (opener || lastFocus)?.focus?.();
  }

  /* ---------------- Toast + screen-reader announcements ---------------- */
  const toast = (msg) => {
    if (!msg) return;
    let el = document.querySelector('.hc-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'hc-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.textContent = msg;
    el.classList.add('is-visible');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-visible'), 2400);
  };

  const announce = (msg) => {
    if (!msg) return;
    let el = document.querySelector('.hc-sr-live');
    if (!el) {
      el = document.createElement('div');
      el.className = 'hc-visually-hidden hc-sr-live';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.textContent = '';
    setTimeout(() => { el.textContent = msg; }, 60);
  };

  /* ---------------- Cart state ---------------- */
  const root = () => window.Shopify?.routes?.root || '/';
  let lastCart = null;
  const cartIndex = new Map(); // variantId -> { key, lineQty, total }

  const indexCart = (cart) => {
    cartIndex.clear();
    (cart?.items || []).forEach(line => {
      const id = String(line.variant_id);
      const entry = cartIndex.get(id);
      if (entry) entry.total += line.quantity;
      else cartIndex.set(id, { key: line.key, lineQty: line.quantity, total: line.quantity });
    });
  };

  const fetchCart = async () => {
    const res = await fetch(`${root()}cart.js`, { cache: 'no-store', credentials: 'same-origin' });
    if (!res.ok) throw new Error('cart');
    return res.json();
  };

  const updateCartCount = (section, cart) => {
    const sel = section?.dataset.cartCountSelector;
    if (!sel) return;
    try { document.querySelectorAll(sel).forEach(el => { el.textContent = cart.item_count; }); } catch (_) {}
  };

  /* ---------------- Card CTA states: idle → prompt → bar ---------------- */
  const setQty = (cta, qty) => {
    cta.dataset.qty = qty;
    const val = cta.querySelector('[data-hc-qty-val]');
    if (val) val.textContent = qty;
    const minus = cta.querySelector('[data-hc-qty="-1"]');
    if (minus) minus.setAttribute('aria-label', qty <= 1 ? minus.dataset.labelRemove : minus.dataset.labelDecrease);
  };

  const setState = (cta, state) => {
    if (cta.dataset.state === state) return;
    const hadFocus = cta.contains(document.activeElement);
    cta.dataset.state = state;
    if (!hadFocus) return;
    // Keep keyboard focus inside the card when the visible controls swap
    const target =
      state === 'idle' ? cta.querySelector('.hc-cta__idle button') :
      state === 'prompt' ? cta.querySelector('.hc-cta__prompt [data-hc-checkout]') :
      cta.querySelector('.hc-cta__bar [data-hc-qty="1"]');
    target?.focus({ preventScroll: true });
  };

  // Bring every prompt-style card in line with the cart
  const syncCards = (scope = document, { collapsePrompt = false } = {}) => {
    scope.querySelectorAll(`${CTA}[data-cta-style="prompt"][data-variant-id]`).forEach(cta => {
      const entry = cartIndex.get(cta.dataset.variantId);
      if (entry && entry.total > 0) {
        setQty(cta, entry.total);
        if (cta.dataset.state === 'idle' || (collapsePrompt && cta.dataset.state === 'prompt')) setState(cta, 'bar');
      } else {
        setQty(cta, 0);
        setState(cta, 'idle');
      }
    });
  };

  const applyCart = (cart, { notify = true, item = null, collapsePrompt = false } = {}) => {
    lastCart = cart;
    indexCart(cart);
    document.querySelectorAll(SEL).forEach(section => {
      updateCartCount(section, cart);
      syncCards(section, { collapsePrompt });
    });
    if (!notify) return;
    // Let the theme's own cart drawer / bubble react
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, item, source: SOURCE } }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { source: SOURCE } }));
    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, productVariantId: item?.variant_id, cartData: cart });
    }
  };

  const refreshCart = async (opts = {}) => {
    try { applyCart(await fetchCart(), { ...opts, notify: false }); } catch (_) {}
  };

  const initCtas = (scope) => {
    if (!scope.querySelector(`${CTA}[data-cta-style="prompt"][data-variant-id]`)) return;
    if (lastCart) syncCards(scope); else refreshCart();
  };

  const setBusy = (btn, cta, on) => {
    btn?.classList.toggle('is-busy', on);
    if (on) btn?.setAttribute('aria-busy', 'true'); else btn?.removeAttribute('aria-busy');
    cta?.classList.toggle('is-busy', on);
  };

  const errorText = async (res, fallback) => {
    try {
      const data = await res.json();
      return data.description || data.message || fallback;
    } catch (_) { return fallback; }
  };

  /* Add to cart (both styles) / Buy now (stacked style) */
  const quickAdd = async (form, section, submitter) => {
    const cta = form.closest(CTA);
    const btn = submitter && form.contains(submitter) ? submitter : form.querySelector('button[type="submit"]');
    if (!btn || btn.disabled || cta?.classList.contains('is-busy')) return;
    const action = btn.value || 'add';
    const style = cta?.dataset.ctaStyle || 'prompt';

    setBusy(btn, cta, true);
    let redirecting = false;
    try {
      const res = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      if (!res.ok) {
        toast(await errorText(res, section.dataset.errorMessage));
        return;
      }
      const item = await res.json();

      if (action === 'buy') {
        // Keep the loader running while checkout loads
        redirecting = true;
        window.location.href = `${root()}checkout`;
        return;
      }

      applyCart(await fetchCart(), { item });

      if (style === 'prompt' && cta) {
        setState(cta, 'prompt');
        announce(section.dataset.promptMessage);
      } else {
        const label = btn.querySelector('.hc-card__qa-text');
        btn.classList.add('is-done');
        if (label && section.dataset.addedLabel) label.textContent = section.dataset.addedLabel;
        setTimeout(() => {
          btn.classList.remove('is-done');
          if (label?.dataset.label) label.textContent = label.dataset.label;
        }, 1600);
        toast(section.dataset.addedMessage);
      }
    } catch (e) {
      toast(section.dataset.errorMessage);
    } finally {
      if (!redirecting) setBusy(btn, cta, false);
    }
  };

  /* Quantity stepper: +1 / −1 (going below 1 removes the item) */
  const changeQty = async (cta, delta, section) => {
    if (cta.classList.contains('is-busy')) return;
    const id = cta.dataset.variantId;
    const entry = cartIndex.get(id);
    if (!entry) { await refreshCart(); return; }

    const before = entry.total;
    const target = Math.max(0, entry.lineQty + delta);
    cta.classList.add('is-busy');
    try {
      const res = await fetch(`${root()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: entry.key, quantity: target }),
      });
      if (!res.ok) {
        toast(await errorText(res, section.dataset.errorMessage));
        await refreshCart();
        return;
      }
      applyCart(await res.json());
      const after = cartIndex.get(id)?.total || 0;
      if (delta > 0 && after <= before) toast(section.dataset.maxQtyMessage);
    } catch (e) {
      toast(section.dataset.errorMessage);
      await refreshCart();
    } finally {
      cta.classList.remove('is-busy');
    }
  };

  /* Browser back (bfcache restore): clear loaders and re-sync with the cart */
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    document.querySelectorAll(`${SEL} .hc-card__qa, ${SEL} ${CTA}, ${SEL} [data-hc-checkout]`).forEach(el => {
      el.classList.remove('is-busy', 'is-done', 'is-loading');
      el.removeAttribute('aria-busy');
      const l = el.querySelector?.('.hc-card__qa-text');
      if (l?.dataset.label) l.textContent = l.dataset.label;
    });
    if (!document.querySelector(SEL)) return;
    refreshCart({ collapsePrompt: true }).then(() => {
      if (!lastCart) return;
      document.querySelectorAll(SEL).forEach(s => updateCartCount(s, lastCart));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { source: SOURCE } }));
    });
  });

  /* Cart changed somewhere else (theme cart drawer, PDP, etc.) */
  document.addEventListener('cart:updated', (e) => {
    if (e.detail?.source === SOURCE) return;
    if (!document.querySelector(`${SEL} ${CTA}[data-cta-style="prompt"]`)) return;
    if (Array.isArray(e.detail?.cart?.items)) applyCart(e.detail.cart, { notify: false });
    else refreshCart();
  });
  const subscribeToTheme = () => {
    if (typeof window.subscribe !== 'function' || !window.PUB_SUB_EVENTS?.cartUpdate) return;
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (ev) => {
      if (ev?.source === SOURCE) return;
      if (document.querySelector(`${SEL} ${CTA}[data-cta-style="prompt"]`)) refreshCart();
    });
  };

  /* ---------------- Delegated events (survive re-render) ---------------- */
  document.addEventListener('click', (e) => {
    const section = e.target.closest(SEL);
    if (!section) return;

    // Disabled quick-add buttons (Coming soon / Sold out) do nothing
    if (e.target.closest('.hc-card__qa--disabled')) { e.preventDefault(); e.stopPropagation(); return; }

    const cta = e.target.closest(CTA);
    if (cta) {
      if (e.target.closest('[data-hc-continue]')) { setState(cta, 'bar'); return; }
      const qtyBtn = e.target.closest('[data-hc-qty]');
      if (qtyBtn) { changeQty(cta, Number(qtyBtn.dataset.hcQty), section); return; }
      const checkout = e.target.closest('[data-hc-checkout]');
      if (checkout) { checkout.classList.add('is-loading'); return; } // normal navigation
    }

    const pill = e.target.closest('[data-hc-pill]');
    if (pill) { applyPill(section, pill, true); return; }

    if (e.target.closest('[data-hc-drawer-open]')) { openDrawer(section); return; }
    if (e.target.closest('[data-hc-drawer-close]')) { e.preventDefault(); closeDrawer(section); return; }

    const link = e.target.closest('a[data-hc-link]');
    if (link && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
      e.preventDefault();
      renderFromUrl(section, link.href);
      if (link.closest('.hc__pagination')) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  const onFilterChange = debounce((form) => {
    const section = form.closest(SEL);
    if (section) renderFromUrl(section, formToUrl(form));
  }, 450);

  document.addEventListener('change', (e) => {
    const form = e.target.closest('[data-hc-filter-form]');
    if (form) onFilterChange(form);
  });
  document.addEventListener('input', (e) => {
    if (e.target.matches('[data-hc-filter-form] input[type="number"]')) onFilterChange(e.target.form);
  });
  document.addEventListener('submit', (e) => {
    const filterForm = e.target.closest('[data-hc-filter-form]');
    if (filterForm) { e.preventDefault(); onFilterChange(filterForm); return; }
    const qa = e.target.closest('[data-hc-quick-add]');
    if (qa) { e.preventDefault(); quickAdd(qa, qa.closest(SEL), e.submitter); }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    // Escape inside the "Added to your cart" prompt = Continue browsing
    const promptCta = document.activeElement?.closest?.(`${CTA}[data-state="prompt"]`);
    if (promptCta) { setState(promptCta, 'bar'); return; }
    document.querySelectorAll(SEL).forEach(closeDrawer);
  });

  window.addEventListener('popstate', () => {
    const section = document.querySelector(SEL);
    if (section) renderFromUrl(section, window.location.href, false);
  });

  /* ---------------- Init + theme editor support ---------------- */
  const initAll = () => {
    document.querySelectorAll(SEL).forEach(s => { initPills(s); initCtas(s); });
    subscribeToTheme();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll); else initAll();

  document.addEventListener('shopify:section:load', (e) => {
    const s = e.target.querySelector(SEL);
    if (s) { initPills(s); initCtas(s); }
  });
  document.addEventListener('shopify:block:select', (e) => {
    const pill = e.target.closest?.('[data-hc-pill]');
    const section = e.target.closest?.(SEL);
    if (pill && section) applyPill(section, pill, false);
  });
})();